import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditTreeModal = ({ tree, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: tree.name,
    scientific_name: tree.scientific_name,
    family: tree.family,
    description: tree.description,
    science_details: tree.science_details,
    habitat: tree.habitat,
    conservation_status: tree.conservation_status,
    lat: tree.location.lat,
    lng: tree.location.lng,
    city: tree.city || '',
    image_url: tree.image_url,
  });
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(tree.image_url);
  const [uploadingImage, setUploadingImage] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image file must be less than 10MB');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return { url: formData.image_url, s3_key: null };

    setUploadingImage(true);
    try {
      const token = localStorage.getItem('admin_token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', imageFile);

      const response = await axios.post(
        `${API}/upload/image`,
        formDataUpload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return {
        url: response.data.url,
        s3_key: response.data.s3_key
      };
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
      throw error;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Upload image first if new file is selected
      let imageUrl = formData.image_url;
      let s3ImageKey = null;
      
      if (imageFile) {
        const uploadResult = await uploadImage();
        imageUrl = uploadResult.url;
        s3ImageKey = uploadResult.s3_key;
      }

      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/trees/${tree.id}`,
        {
          ...formData,
          image_url: imageUrl,
          s3_image_key: s3ImageKey,
          lat: parseFloat(formData.lat),
          lng: parseFloat(formData.lng),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Tree updated successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error updating tree:', error);
      toast.error('Failed to update tree. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="edit-tree-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-heading font-bold text-primary">Edit Tree</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary rounded-lg transition-colors"
            data-testid="close-modal-button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6" data-testid="edit-tree-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Common Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Scientific Name
              </label>
              <input
                type="text"
                name="scientific_name"
                value={formData.scientific_name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="scientific-name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Family
              </label>
              <input
                type="text"
                name="family"
                value={formData.family}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="family-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Habitat
              </label>
              <input
                type="text"
                name="habitat"
                value={formData.habitat}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="habitat-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Conservation Status
              </label>
              <input
                type="text"
                name="conservation_status"
                value={formData.conservation_status}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="conservation-status-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                City/Town
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="e.g., Sydney, Melbourne, Brisbane"
                data-testid="city-input"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                Tree Image
              </label>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <label className="flex-1 cursor-pointer">
                    <div className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-input rounded-lg hover:border-primary transition-colors">
                      <Upload className="w-5 h-5 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {imageFile ? imageFile.name : 'Upload New Image (Max 10MB)'}
                      </span>
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleImageChange}
                      className="hidden"
                      data-testid="image-file-input"
                    />
                  </label>
                </div>
                
                {imagePreview && (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    {imageFile && (
                      <button
                        type="button"
                        onClick={() => {
                          setImageFile(null);
                          setImagePreview(tree.image_url);
                        }}
                        className="absolute top-2 right-2 p-1 bg-destructive text-white rounded-full hover:bg-destructive/90"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Latitude
              </label>
              <input
                type="number"
                step="any"
                name="lat"
                value={formData.lat}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="latitude-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Longitude
              </label>
              <input
                type="number"
                step="any"
                name="lng"
                value={formData.lng}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="longitude-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="description-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Scientific Details
            </label>
            <textarea
              name="science_details"
              value={formData.science_details}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="science-details-input"
            />
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-border rounded-lg font-medium hover:bg-secondary transition-colors"
              data-testid="cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || uploadingImage}
              className="px-6 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-50"
              data-testid="submit-button"
            >
              {loading ? 'Updating...' : uploadingImage ? 'Uploading...' : 'Update Tree'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTreeModal;